// Public API client - only hits /api/public/* endpoints.
//
// No auth, no cookies, no admin routes. The backend filters which paths
// are reachable from the public domain via Caddy.

export interface SnapshotMeta {
  ready: boolean;
  generated_at?: string;
  lookback_days?: number;
  exit_pct_of_peak?: number;
  max_multiplier_cap?: number;
  per_trade_usd?: number;
  runtime_secs?: number;
  total_strategies?: number;
  total_signals_30d?: number;
}

export interface PublicStrategyFilters {
  lookback_days: number;
  min_wallets: number;
  mc_min_usd: number | null;
  mc_max_usd: number | null;
  peak_mc_max_usd: number | null;
  liq_min_usd: number | null;
  liq_max_usd: number | null;
  age_max_mins: number | null;
  chains: string[];
  wallet_whitelist: string[] | null;
  wallet_blacklist: string[] | null;
  exit_pct_of_peak: number;
  max_multiplier_cap: number | null;
  per_trade_usd: number;
  fee_pct: number;
}

export interface PublicStrategy {
  rank: number;
  label: string;
  filters: PublicStrategyFilters;
  signals_count: number;
  median_multiplier: number;
  pct_2x: number;
  pct_5x: number;
  pct_10x: number;
  pnl_total_usd: number;
  win_rate_pct: number;
}

export interface SnapshotResponse {
  items: PublicStrategy[];
  meta: SnapshotMeta | null;
  filtered_count: number;
  total_count: number;
  min_wallets_filter: number;
}

// Signal report (Tweet Studio report card numbers): every token called in
// the window counted once from its first call. Aggregate only.
export type ReportWindow = "7d" | "30d";
export interface SignalReport {
  window: ReportWindow;
  days: number;
  delay_hours: number;
  since: string;
  until: string;
  called: number;
  peaked_2x: number;
  peaked_5x: number;
  peaked_10x: number;
  peaked_20x: number;
  peaked_50x: number;
  never_ran: number;
  generated_at: string;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function jsonGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore non-json */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

// ─── On-demand backtest types ────────────────────────────────────

export interface BacktestFilters {
  lookback_days: number;
  min_wallets: number;
  mc_min_usd: number | null;
  mc_max_usd: number | null;
  peak_mc_max_usd: number | null;
  liq_min_usd: number | null;
  liq_max_usd: number | null;
  age_max_mins: number | null;
  chains: string[];
  exit_pct_of_peak: number;
  max_multiplier_cap: number | null;
  per_trade_usd: number;
  fee_pct: number;
  // ★ quality tiers (0 = Neutral … 3 = ★★★); null = all signals.
  quality_tiers: number[] | null;
}

export interface HitBucket {
  label: string;
  count: number;
  pct: number;
}

export interface PnlPoint {
  idx: number;
  cum_pnl_usd: number;
}

export interface BacktestResult {
  signals_count: number;
  median_multiplier: number;
  avg_multiplier: number;
  pct_2x: number;
  pct_5x: number;
  pct_10x: number;
  pnl_total_usd: number;
  pnl_gross_usd: number;
  fees_paid_usd: number;
  win_count: number;
  loss_count: number;
  win_rate_pct: number;
  hit_distribution: HitBucket[];
  pnl_curve: PnlPoint[];
  filters_echo: BacktestFilters;
}

async function jsonPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* ignore non-json */
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

// ─── HODL snapshot ─────────────────────────────────────────────

export interface HodlMeta {
  generated_at?: string;
  lookback_days?: number;
  min_wallets?: number;
  per_trade_usd?: number;
  tokens_count?: number;
  tokens_with_price?: number;
  runtime_secs?: number;
}

export interface HodlSnapshot {
  ready: boolean;
  meta: HodlMeta | null;
  quality_tiers: number[] | null;
  total_pnl_usd: number;
  total_gross_usd: number;
  total_fees_usd: number;
  total_invested_usd: number;
  tokens_count: number;
  tokens_with_price: number;
  win_count: number;
  loss_count: number;
  win_rate_pct: number;
  median_multiplier: number;
  hit_distribution: HitBucket[];
  per_trade_usd: number;
  fee_pct: number;
}

// ─── Public Tokens list ─────────────────────────────────────────

export interface PublicTokenRow {
  symbol: string;
  chain: string;
  mc_at_trigger: number;
  peak_mc: number;
  multiplier: number;
  days_ago: number;
}

export interface PublicTokensResponse {
  items: PublicTokenRow[];
  count: number;
  has_more: boolean;
  days_window: number;
  delay_hours: number;
  sort: "recent" | "peak";
  min_wallets_filter: number;
}

export interface TokensQuery {
  days?: number;
  // Admin-only zincirler (<CHAIN>_SIGNALS_ADMIN_ONLY=true) backend'de public
  // /tokens + backtest sonuçlarından eleniyor. bnb (2026-08-04) ve arc
  // go-public oldu → FilterPanel/Tokens/Strategies butonları ikisinde de var.
  // Yeni bir zincir admin-only başlarsa tipi erken ekle, butonu go-public'e
  // kadar EKLEME (bayrak açıkken buton hep boş liste döndürür).
  chain?: "base" | "ethereum" | "solana" | "robinhood" | "bnb" | "arc" | null;
  min_wallets?: number;
  limit?: number;
  offset?: number;
  sort?: "recent" | "peak";
}

export const publicApi = {
  meta: () => jsonGet<SnapshotMeta>("/api/public/meta"),
  report: (window: ReportWindow) => jsonGet<SignalReport>(`/api/public/report?window=${window}`),
  strategies: (minWallets: number) =>
    jsonGet<SnapshotResponse>(
      `/api/public/strategy-snapshot?min_wallets=${minWallets}`,
    ),
  backtest: (filters: BacktestFilters) =>
    jsonPost<BacktestResult>("/api/public/backtest", filters),
  hodl: (minWallets: number = 3, days: number = 30, quality: number[] = []) =>
    jsonGet<HodlSnapshot>(
      `/api/public/hodl-snapshot?min_wallets=${minWallets}&days=${days}` +
        (quality.length ? `&quality=${quality.join(",")}` : ""),
    ),
  tokens: (q: TokensQuery = {}) => {
    const p = new URLSearchParams();
    if (q.days != null) p.set("days", String(q.days));
    if (q.chain) p.set("chain", q.chain);
    if (q.min_wallets != null) p.set("min_wallets", String(q.min_wallets));
    if (q.limit != null) p.set("limit", String(q.limit));
    if (q.offset != null) p.set("offset", String(q.offset));
    if (q.sort) p.set("sort", q.sort);
    const qs = p.toString();
    return jsonGet<PublicTokensResponse>(
      `/api/public/tokens${qs ? "?" + qs : ""}`,
    );
  },
};

export { ApiError };
